import React, { useEffect, useState } from 'react';
import { ArrowUp, ArrowDown } from 'lucide-react';
interface ScoreHeatmapProps {
  scores: Record<string, Record<string, any>>;
  scoreGeral: Record<string, number>;
  mainBrand: string;
  attributes: string[];
  findCompetitorUrl?: (brand: string) => string;
  getFavicon?: (url: string) => string;
}
const ScoreHeatmap: React.FC<ScoreHeatmapProps> = ({
  scores,
  scoreGeral,
  mainBrand,
  attributes,
  findCompetitorUrl,
  getFavicon
}) => {
  const [showMainBrandFirst, setShowMainBrandFirst] = useState(true);
  const [sortColumn, setSortColumn] = useState<string>('scoreGeral');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [minMaxScores, setMinMaxScores] = useState<Record<string, {
    min: number;
    max: number;
  }>>({});
  // Get score value safely (handles both old and new format)
  const getScore = (brand: string, attribute: string) => {
    const value = scores[brand][attribute];
    if (typeof value === 'number') {
      return value;
    } else if (typeof value === 'object' && value !== null && 'score' in value) {
      return value.score;
    }
    return 0;
  };
  // Load user preferences from localStorage on component mount
  useEffect(() => {
    const savedShowMainBrandFirst = localStorage.getItem('showMainBrandFirst');
    const savedSortColumn = localStorage.getItem('sortColumn');
    const savedSortDirection = localStorage.getItem('sortDirection');
    if (savedShowMainBrandFirst !== null) {
      setShowMainBrandFirst(savedShowMainBrandFirst === 'true');
    }
    if (savedSortColumn) {
      setSortColumn(savedSortColumn);
    }
    if (savedSortDirection) {
      setSortDirection(savedSortDirection as 'asc' | 'desc');
    }
  }, []);
  // Calculate min and max scores for each attribute for relative coloring
  useEffect(() => {
    const attributeMinMax: Record<string, {
      min: number;
      max: number;
    }> = {};
    // Initialize with scoreGeral
    attributeMinMax['scoreGeral'] = {
      min: Math.min(...Object.values(scoreGeral)),
      max: Math.max(...Object.values(scoreGeral))
    };
    // Calculate for each attribute
    attributes.forEach(attribute => {
      const values = Object.entries(scores).map(brandScores => getScore(brandScores[0], attribute));
      attributeMinMax[attribute] = {
        min: Math.min(...values),
        max: Math.max(...values)
      };
    });
    setMinMaxScores(attributeMinMax);
  }, [scores, scoreGeral, attributes]);
  // Save preferences to localStorage when they change
  useEffect(() => {
    localStorage.setItem('showMainBrandFirst', String(showMainBrandFirst));
    localStorage.setItem('sortColumn', sortColumn);
    localStorage.setItem('sortDirection', sortDirection);
  }, [showMainBrandFirst, sortColumn, sortDirection]);
  // Sort brands based on current settings
  const sortedBrands = Object.keys(scores).sort((a, b) => {
    // If showing main brand first, it always comes first
    if (showMainBrandFirst) {
      if (a === mainBrand) return -1;
      if (b === mainBrand) return 1;
    }
    // If sorting by score geral
    if (sortColumn === 'scoreGeral') {
      const aScore = scoreGeral[a] || 0;
      const bScore = scoreGeral[b] || 0;
      // If scores are equal, prioritize the main brand
      if (aScore === bScore) {
        if (a === mainBrand) return sortDirection === 'asc' ? 1 : -1;
        if (b === mainBrand) return sortDirection === 'asc' ? -1 : 1;
      }
      return sortDirection === 'asc' ? aScore - bScore : bScore - aScore;
    }
    // If sorting by an attribute
    const aScore = getScore(a, sortColumn);
    const bScore = getScore(b, sortColumn);
    // If scores are equal, prioritize the main brand
    if (aScore === bScore) {
      if (a === mainBrand) return sortDirection === 'asc' ? 1 : -1;
      if (b === mainBrand) return sortDirection === 'asc' ? -1 : 1;
    }
    return sortDirection === 'asc' ? aScore - bScore : bScore - aScore;
  });
  // Get color based on score value relative to min/max for that attribute
  const getBackgroundColor = (score: number, attribute: string) => {
    const {
      min,
      max
    } = minMaxScores[attribute] || {
      min: 0,
      max: 100
    };
    // If all scores are the same, use middle color
    if (min === max) return '#fef7cd'; // Middle color for equal scores
    // Calculate relative position between min and max (0 to 1)
    const range = max - min;
    const relativeScore = (score - min) / range;
    // Use the new color palette
    const colors = ['#fce7f3', '#fed7d7', '#fef7cd', '#f0fdf4', '#dcfce7', '#bbf7d0'];
    // Find which segment of the gradient the score falls into
    const segmentSize = 1 / (colors.length - 1);
    const segmentIndex = Math.min(Math.floor(relativeScore / segmentSize), colors.length - 2);
    // Calculate position within the segment (0 to 1)
    const segmentPosition = (relativeScore - segmentIndex * segmentSize) / segmentSize;
    // Return the appropriate color from the palette based on score
    return colors[Math.min(Math.floor(relativeScore * colors.length), colors.length - 1)];
  };
  // Always use dark text regardless of background
  const getTextColor = () => {
    return 'text-gray-900';
  };
  // Format number with comma as decimal separator and no decimal places for whole numbers
  const formatNumber = (num: number) => {
    return num % 1 === 0 ? num.toString() : num.toFixed(1).replace('.', ',');
  };
  // Find the winner (highest score) for each attribute and for general score
  const getWinners = () => {
    const winners: Record<string, string[]> = {
      scoreGeral: []
    };
    // Find winners for general score
    let highestGeneralScore = -1;
    Object.entries(scoreGeral).forEach(([brand, score]) => {
      if (score > highestGeneralScore) {
        highestGeneralScore = score;
        winners.scoreGeral = [brand];
      } else if (score === highestGeneralScore && highestGeneralScore > 0) {
        winners.scoreGeral.push(brand);
      }
    });
    // Find winners for each attribute
    attributes.forEach(attribute => {
      let highestScore = -1;
      let winningBrands: string[] = [];
      Object.entries(scores).forEach(([brand, brandScores]) => {
        const score = getScore(brand, attribute);
        if (score > highestScore) {
          highestScore = score;
          winningBrands = [brand];
        } else if (score === highestScore && highestScore > 0) {
          winningBrands.push(brand);
        }
      });
      winners[attribute] = winningBrands;
    });
    return winners;
  };
  const winners = getWinners();
  // Handle column sort when clicking on header
  const handleColumnSort = (column: string) => {
    if (sortColumn === column) {
      // Toggle direction if same column
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // New column, default to descending
      setSortColumn(column);
      setSortDirection('desc');
    }
  };
  return <div className="w-full" data-id="element-814">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-2" data-id="element-815">
        <div className="flex items-center" data-id="element-816">
          {/* Show main brand first checkbox */}
          <div className="flex items-center" data-id="element-817">
            <input type="checkbox" id="showMainBrandFirst" checked={showMainBrandFirst} onChange={() => setShowMainBrandFirst(!showMainBrandFirst)} className="mr-2 h-4 w-4 text-[#1649ff] focus:ring-[#1649ff] border-gray-300 rounded" data-id="element-818" />
            <label htmlFor="showMainBrandFirst" className="text-sm text-gray-900" data-id="element-819">
              Exibir minha marca primeiro
            </label>
          </div>
        </div>
      </div>

      {/* Enhanced Heatmap with fixed first column */}
      <div className="relative overflow-hidden border border-gray-200 rounded-lg" data-id="element-820">
        <div className="overflow-x-auto" data-id="element-821">
          <table className="min-w-full divide-y divide-gray-200" data-id="element-822">
            <thead className="bg-gray-50" data-id="element-823">
              <tr data-id="element-824">
                {/* Fixed first column header */}
                <th className="sticky left-0 z-20 px-4 py-3 text-left text-xs font-medium text-gray-800 uppercase tracking-wider bg-gray-50 border-b border-r border-gray-200" style={{
                minWidth: '140px'
              }} data-id="element-825">
                  Marca
                </th>
                {/* Score Geral header */}
                <th onClick={() => handleColumnSort('scoreGeral')} className="px-4 py-3 text-left text-xs font-medium text-gray-800 uppercase tracking-wider cursor-pointer hover:bg-gray-100 border-b border-gray-200" data-id="element-826">
                  <div className="flex items-center" data-id="element-827">
                    Score Geral
                    {sortColumn === 'scoreGeral' && <span className="ml-1" data-id="element-828">
                        {sortDirection === 'asc' ? <ArrowUp size={14} data-id="element-829" /> : <ArrowDown size={14} data-id="element-830" />}
                      </span>}
                  </div>
                </th>
                {/* Attribute headers */}
                {attributes.map(attribute => <th key={attribute} onClick={() => handleColumnSort(attribute)} className="px-4 py-3 text-left text-xs font-medium text-gray-800 uppercase tracking-wider cursor-pointer hover:bg-gray-100 border-b border-gray-200" data-id="element-831">
                    <div className="flex items-center" data-id="element-832">
                      {attribute}
                      {sortColumn === attribute && <span className="ml-1" data-id="element-833">
                          {sortDirection === 'asc' ? <ArrowUp size={14} data-id="element-834" /> : <ArrowDown size={14} data-id="element-835" />}
                        </span>}
                    </div>
                  </th>)}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-836">
              {sortedBrands.map(brand => <tr key={brand} className="hover:bg-gray-50" data-id="element-837">
                  {/* Fixed first column with brand name */}
                  <td className="sticky left-0 z-10 px-4 py-3 text-sm font-medium text-gray-900 whitespace-nowrap bg-white border-r border-gray-200" data-id="element-838">
                    <div className="flex items-center" data-id="element-839">
                      {findCompetitorUrl && getFavicon && <img src={getFavicon(findCompetitorUrl(brand))} alt={`${brand} favicon`} className="w-4 h-4 mr-2" onError={e => {
                    e.currentTarget.onerror = null;
                    e.currentTarget.src = 'https://www.google.com/s2/favicons?domain=example.com&sz=128';
                  }} data-id="element-840" />}
                      {brand}
                    </div>
                  </td>
                  {/* Score Geral cell */}
                  <td className="p-0 whitespace-nowrap text-sm text-center" data-id="element-841">
                    <div className="h-full w-full flex items-center justify-center py-3 text-gray-900" style={{
                  backgroundColor: getBackgroundColor(scoreGeral[brand] || 0, 'scoreGeral')
                }} data-id="element-842">
                      <div className="flex items-center" data-id="element-843">
                        {winners.scoreGeral?.includes(brand) && <span className="mr-1 text-yellow-600" data-id="element-844">🏆</span>}
                        <span className="font-medium" data-id="element-845">
                          {formatNumber(scoreGeral[brand] || 0)}
                        </span>
                      </div>
                    </div>
                  </td>
                  {/* Attribute score cells */}
                  {attributes.map(attribute => {
                const score = getScore(brand, attribute);
                const isWinner = winners[attribute]?.includes(brand);
                return <td key={`${brand}-${attribute}`} className="p-0 whitespace-nowrap text-sm text-center" data-id="element-846">
                        <div className="h-full w-full flex items-center justify-center py-3 text-gray-900" style={{
                    backgroundColor: getBackgroundColor(score, attribute)
                  }} data-id="element-847">
                          <div className="flex items-center" data-id="element-848">
                            {isWinner && <span className="mr-1 text-yellow-600" data-id="element-849">🏆</span>}
                            <span className="font-medium" data-id="element-850">
                              {formatNumber(score)}
                            </span>
                          </div>
                        </div>
                      </td>;
              })}
                </tr>)}
            </tbody>
          </table>
        </div>
      </div>

      {/* Color legend */}
      <div className="mt-6 flex items-center justify-center" data-id="element-851">
        <div className="flex items-center space-x-2" data-id="element-852">
          <span className="text-xs text-gray-900" data-id="element-853">Menor pontuação</span>
          <div className="w-40 h-4 rounded bg-gradient-to-r from-[#fce7f3] via-[#fed7d7] via-[#fef7cd] via-[#f0fdf4] via-[#dcfce7] to-[#bbf7d0]" style={{
          background: 'linear-gradient(90deg, #fce7f3 0%, #fed7d7 20%, #fef7cd 40%, #f0fdf4 60%, #dcfce7 80%, #bbf7d0 100%)'
        }} data-id="element-854"></div>
          <span className="text-xs text-gray-900" data-id="element-855">Maior pontuação</span>
        </div>
      </div>

      <div className="mt-2 flex justify-center" data-id="element-856">
        <div className="flex items-center" data-id="element-857">
          <span className="mr-1 text-yellow-600" data-id="element-858">🏆</span>
          <span className="text-xs text-gray-800" data-id="element-859">Melhor pontuação</span>
        </div>
      </div>
    </div>;
};
export default ScoreHeatmap;